import { RxState } from "@rx-angular/state";
import { rxActions } from "@rx-angular/state/actions";
import { DestroyRef, Inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, filter, finalize, first, map, Observable, of, startWith, Subject, switchMap, tap } from "rxjs";

import { LoggerService } from "../../../../src/app/logger/logger.service";
import { RiskCatalog, RiskRateEnum, RiskRegistryColumnEnum, RiskRegistryFilteringGroupEnum, RiskRegistryFilters } from "src/app/api/models/risks/risks.interface";        
import { RiskRegistryTableState } from "../models/risks-registry-table.state.interface";
import { TableColumn } from "src/app/shared/models/table-column.interface";
import { ContentPipe } from "src/app/services/content/content.pipe";
import { RiskRegistryActions } from "../models/risk-registry-actions.interface";
import { FilterCategory } from "src/app/core/models/filtering.interface";
import { SystemInstance } from "src/app/systems/models/systems.interface";
import { SentenceCasePipe } from "src/app/shared/pipes/sentence-case.pipe";
import { AssessmentsService } from "src/app/ai-assessments/services/assessments.service";
import { RisksCatalogService } from "../services/risks-catalog.service";
import { CustomValuesQuery } from "src/app/company-settings/state/custom-values/custom-values.query";
import { CustomValueTypeEnum } from "src/app/api/models/company-settings/custom-values.enum";
import { CustomValue } from "src/app/api/models/company-settings/custom-values.interface";
import { FeatureFlags } from "src/app/api/models/profile/profile-feature-flags.enum";
import { FeatureFlagQuery } from "src/app/feature-flag/state/feature-flag.query";
import { API_CLIENT_RISKS_SERVICE } from "src/app/api/injectors/api-client-risks.token";
import { BaseApiClientRisksService } from "src/app/api/base-api-client-risks.service";
import { MineSort } from "src/app/shared/mine-sort/mine-sort.interface";
import { SortDirectionEnum } from "src/app/shared/sort-direction.enum";
import { AssessmentPage } from "src/app/ai-assessments/models/assessments.interface";
import { RiskCalulationToTextPipe } from "src/app/ai-assessments/pipes/risk-calculation-to-text.pipe";
import { AssessmentCustomLabel, AssessmentResourceId, AssessmentTag } from "src/app/api/models/assessments/assessment.interface";
import { Risk } from "../models/risks.interface";

@Injectable({
    providedIn: 'root'
})
export class RiskRegistryTableStore extends RxState<RiskRegistryTableState> {

    private readonly loggerName: string = 'RiskRegistryTableStore';

    private readonly defaultSort: MineSort = { active: RiskRegistryColumnEnum.Created, direction: SortDirectionEnum.Desc };

    private readonly initState = {
        empty: undefined,
        loading: true,
        disabled: false,
        riskRegistry: undefined,
        columns: undefined,
        filters: undefined,
        search: undefined,
        sort: this.defaultSort,
        view: undefined,
    } as RiskRegistryTableState;

    public actions = rxActions<RiskRegistryActions>();

    private initial$ = new BehaviorSubject<Map<number, Risk>>(null);
    /** Re-runs both fetches (filtered registry + unfiltered counts) so the page shows the server state on entry. */
    private readonly reload$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        @Inject(API_CLIENT_RISKS_SERVICE) private apiClientRisksService: BaseApiClientRisksService,
        private contentPipe: ContentPipe,
        private destroyRef: DestroyRef,
        private sentenceCasePipe: SentenceCasePipe,
        private assessmentsService: AssessmentsService,
        private risksCatalogService: RisksCatalogService,
        private customValuesQuery: CustomValuesQuery,
        private featureFlagQuery: FeatureFlagQuery,
        private riskCalulationToTextPipe: RiskCalulationToTextPipe
    ) {
        super();
        this.set(this.initState);
        this.set({ 'columns': this.getColumns() });
        this.connect('filters', this.selectFilters());

        const search$ = this.actions.changeSearchValue$.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
        );

        const filters$ = this.actions.changeFiltersValue$.pipe(
            map(filters => this.getQueryParams(filters)),
            startWith({})
        );

        const sort$ = this.actions.changeSortValue$.pipe(
            startWith(this.defaultSort),
            // The mineSort directive re-emits the seeded default on init; collapse the duplicate so we fetch once.
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        );

        const riskCreated$ = this.actions.riskCreated$.pipe(
            tap(risk => {
                if (risk?.id != null) {
                    // Same join as a full load: the BE returns assessmentPageId only, the Assessment column needs the page.
                    const joined = this.withAssessmentPage({ ...risk as Risk });
                    this.upsertRiskInStore({ ...joined, originalState: joined });
                }
            })
        );

        riskCreated$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        // A deletion drops the row locally instead of refetching: a risk removed from an assessment is only deleted
        // on the BE when the assessment is saved, so an immediate refetch would bring the stale row back.
        this.actions.riskDeleted$.pipe(
            tap(riskId => this.removeRiskFromStore(riskId)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        // Updated (risk panel on any surface).
        this.actions.riskUpdated$.pipe(
            tap(updated => this.applyRiskUpdate(updated as Risk)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        // Explicit reload (registry page entry): refresh the unfiltered counts; the filtered fetch below re-runs too.
        this.reload$.pipe(
            switchMap(() => this.getRisksFromServer()),
            // The unfiltered fetch is the page's initial load: it also clears `loading` (filters/table wait on it).
            tap(risks => {
                this.initial$.next(risks);
                this.set({ loading: false, empty: !risks?.size });
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();

        this.connect(
            'riskRegistry',
            combineLatest([search$, filters$, sort$, this.reload$.pipe(startWith(null))]).pipe(
                switchMap(([search, filters, sort]) => this.getRisksFromServer(search, filters as RiskRegistryFilters, sort)),
            )
        );
    }

    reload(): void {
        this.reload$.next();
    }

    private upsertRiskInStore(risk: Risk): void {
        const withRisk = (risks: Map<number, Risk>): Map<number, Risk> => new Map(risks ?? []).set(Number(risk.id), risk);
        this.initial$.next(withRisk(this.initial$.value));
        const riskRegistry = withRisk(this.get('riskRegistry'));
        this.set({ riskRegistry, empty: !riskRegistry.size });
    }

    private removeRiskFromStore(riskId: AssessmentResourceId): void {
        const without = (risks: Map<number, Risk>): Map<number, Risk> => {
            const next = new Map(risks ?? []);
            next.delete(Number(riskId));
            return next;
        };
        this.initial$.next(without(this.initial$.value));
        const riskRegistry = without(this.get('riskRegistry'));
        this.set({ riskRegistry, empty: !riskRegistry.size });
    }

    getInitialData(): Observable<Map<number, Risk>> {
        return this.initial$.asObservable();
    }

    private getColumns(): Map<string, TableColumn> { 
        let columns: TableColumn[] = [...this.contentPipe.transform('risks.riskRegistry.tableColumns')];
        columns.push(this.contentPipe.transform('risks.riskRegistry.riskRegistryCollaboratorsColumn'));
        columns.push(...this.contentPipe.transform('risks.riskRegistry.riskRegistryAdditionalColumns'));
        const typeColumnIndex = columns.findIndex(column => column.key === RiskRegistryColumnEnum.AssessmentType);

        if (typeColumnIndex !== -1) {
            columns.splice(
                typeColumnIndex + 1,
                0,
                this.contentPipe.transform('risks.riskRegistry.riskRegistryLabelsColumn')
            );
        }

        // DevRiskLabels: the Labels column shows the risk's own labels and must exist even when
        // there is no AssessmentType column to anchor it to.
        if (this.featureFlagQuery.getFlag(FeatureFlags.DevRiskLabels) && !columns.some(column => column.key === RiskRegistryColumnEnum.Labels)) {
            columns.push(this.contentPipe.transform('risks.riskRegistry.riskRegistryLabelsColumn'));
        }

        // The source assessment's type is replaced by its labels, so the AssessmentType column is
        // dropped in favour of the Labels column (already injected above).
        columns = columns.filter(column => column.key !== RiskRegistryColumnEnum.AssessmentType);

        const map = new Map<string, TableColumn>();
        for (let column of columns) {
            map.set(column.key, column);
        }

        return map;
    }

    private getRisksFromServer(search?: string, filters?: RiskRegistryFilters, sort?: MineSort): Observable<Map<number, Risk> | undefined> {
        return this.assessmentsService.selectLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.apiClientRisksService.getRiskRegistry(search, filters, sort)),
            first(),
            map(risks => new Map(risks.map(risk => ([risk.id, risk])))),
            map(risks => this.riskRegistryMapper(risks)),
            catchError(err => {
                console.error(err);
                this.logger.error(this.loggerName, err);
                return of(new Map());
            }),
        );
    }

    private riskRegistryMapper(risks: Map<number, Risk>): Map<number, Risk> {
        const map = new Map<number, Risk>();
        const assessments = this.assessmentsService.getAssessments()() as Map<AssessmentResourceId, AssessmentPage>;

        Array.from(risks.keys()).forEach(key => {
            const riskWithAssessmentPage = this.withAssessmentPage(risks.get(key), assessments);
            map.set(key, {...riskWithAssessmentPage, originalState: riskWithAssessmentPage});
        });
        return map;
    }

    /** Resolves the risk's assessment from the assessments store by `assessmentPageId` (keeps whatever the risk already carries as a fallback). */
    private withAssessmentPage(
        risk: Risk,
        assessments: Map<AssessmentResourceId, AssessmentPage> = this.assessmentsService.getAssessments()() as Map<AssessmentResourceId, AssessmentPage>,
    ): Risk {
        const assessmentId = risk?.assessmentPageId ?? risk?.assessmentPage?.id;
        return { ...risk, assessmentPage: assessments.get(assessmentId) ?? risk.assessmentPage };
    }

    selectFilters(): Observable<FilterCategory[]> {
        // Risks are filtered by the source assessment's labels instead of its type.
        // DevRiskLabels: the Labels filter is risk-scoped (the risk's own tags) and supersedes the
        // assessment-scoped one — so when it's on the assessment-scoped labels filter is dropped.
        const riskLabels = this.featureFlagQuery.getFlag(FeatureFlags.DevRiskLabels);

        return this.select('loading').pipe(
            filter(loading => !loading),
            switchMap(() => combineLatest([
                riskLabels ? of(null) : this.selectLabelsFilterGroup(),
                riskLabels ? this.selectRiskLabelsFilterGroup() : of(null),
                this.selectDataSourcesFilterGroup(),
                this.selectDataTypesFilterGroup(),
                this.selectMitigationsFilterGroup()
            ])),
            map(([typeOrLabelsFilter, riskLabelsFilter, dataSourcesFilter, riskTypesFilter, mitigationsTypesFilter]) => {

                const residualRiskFilter =
                    this.getRiskFilterGroup(RiskRegistryFilteringGroupEnum.ResidualRisk,
                        this.contentPipe.transform('risks.riskRegistry.residualRiskFilterGroup'));

                const inherentRiskFilter =
                    this.getRiskFilterGroup(RiskRegistryFilteringGroupEnum.InherentRisk,
                        this.contentPipe.transform('risks.riskRegistry.inherentRiskFilterGroup'));

                const filters = [inherentRiskFilter, residualRiskFilter];

                if(typeOrLabelsFilter?.options?.length) {
                    filters.push(typeOrLabelsFilter);
                }

                if (riskLabelsFilter?.options?.length) {
                    filters.push(riskLabelsFilter);
                }

                if (dataSourcesFilter?.options?.length) {
                    filters.push(dataSourcesFilter);
                }

                if(riskTypesFilter?.options?.length) {
                    filters.push(riskTypesFilter);
                }

                if(mitigationsTypesFilter?.options?.length) {
                    filters.push(mitigationsTypesFilter);
                }
                
                return filters;
            }),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    private getRiskFilterGroup(id: string, label: string): FilterCategory {
        const options = Object.values(RiskRateEnum);

        return {
            id,
            label,
            options: options.map(rate => ({
                id: rate,
                label: this.riskCalulationToTextPipe.transform(rate),
                selected: false
            }))
        } as FilterCategory;
    }

    private selectLabelsFilterGroup(): Observable<FilterCategory> {
        return this.assessmentsService.selectUniqueAssessmentLabelsWithRisks().pipe(
            first(),
            map(labels => this.getLabelsFilterGroup(labels)),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    /** DevRiskLabels: options = the labels actually attached to risks (from the unfiltered registry). */
    private selectRiskLabelsFilterGroup(): Observable<FilterCategory> {
        return this.selectRiskLabels().pipe(
            takeUntilDestroyed(this.destroyRef),
            first(),
            map(labels => ({
                id: RiskRegistryFilteringGroupEnum.RiskLabels,
                label: this.contentPipe.transform('ai-assessments.labelsFilterGroup'),
                options: labels.map(label => ({ id: String(label.id), label: label.name, selected: false }))
            } as FilterCategory)),
        );
    }

    /** Unique risk-owned labels across the unfiltered registry, most used first. */
    selectRiskLabels(): Observable<AssessmentTag[]> {
        return this.getInitialData().pipe(
            filter(risks => !!risks),
            map(risks => {
                const usage = new Map<AssessmentResourceId, { tag: AssessmentTag, count: number }>();
                Array.from(risks.values()).flatMap(risk => risk.tags ?? []).forEach(tag => {
                    if (tag?.id === null || tag?.id === undefined) return;
                    const entry = usage.get(tag.id) ?? { tag, count: 0 };
                    entry.count++;
                    usage.set(tag.id, entry);
                });
                return Array.from(usage.values())
                    .sort((a, b) => b.count - a.count || a.tag.name?.localeCompare(b.tag.name))
                    .map(entry => entry.tag);
            })
        );
    }

    private selectDataSourcesFilterGroup(): Observable<FilterCategory> {
        return this.assessmentsService.selectUniqueDataSourcesWithRisks().pipe(
            first(),
            map(dataSources => this.getDataSourcesFilterGroup(dataSources)),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    private selectDataTypesFilterGroup(): Observable<FilterCategory> {
        return this.risksCatalogService.selectRisksCatalog(true).pipe(
            first(),
            map(risksCatalog => this.getRiskTypesFilterGroup(risksCatalog as RiskCatalog[])),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    private selectMitigationsFilterGroup(): Observable<FilterCategory> {
        return this.customValuesQuery.selectCustomValuesByType(CustomValueTypeEnum.Mitigation).pipe(
            first(),
            map(mitigations => this.getMitigationsFilterGroup(mitigations)),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    private getLabelsFilterGroup(labels: AssessmentCustomLabel[]): FilterCategory {
        return {
            id: RiskRegistryFilteringGroupEnum.Labels,
            label: this.contentPipe.transform('ai-assessments.labelsFilterGroup'),
            options: (labels ?? [])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(label => ({
                    // id = tag id so the filter sends `relations.tags=<ids>` to the BE (not names)
                    id: String(label.id),
                    label: label.name,
                    selected: false
                }))
        } as FilterCategory;
    }

    private getMitigationsFilterGroup(customValues: CustomValue[]) {
        const predefinedOptions = this.contentPipe.transform('ropa.mitigations')
            .map(({ key, value }) => ({ id: key, label: value}));

        const customOptions = customValues?.map(customValue => ({
            id: customValue.id,
            label: this.sentenceCasePipe.transform(customValue.name),
            selected: false
        }));

        return {
            id: RiskRegistryFilteringGroupEnum.Mitigation,
            label: this.contentPipe.transform('risks.riskRegistry.mitigationFilterGroup'),
            options: [...predefinedOptions, ...customOptions].sort((a, b) => a.label?.localeCompare(b.label)) ?? []
        } as FilterCategory;
    }
    
    private getDataSourcesFilterGroup(dataSources: SystemInstance[]): FilterCategory {
        return {
            id: RiskRegistryFilteringGroupEnum.DataSource,
            label: this.contentPipe.transform('risks.riskRegistry.dataSourceFilterGroup'),
            options: dataSources?.map(dataSource => ({
                id: dataSource.systemId,
                label: this.sentenceCasePipe.transform(dataSource.name),
                selected: false
            })).sort((a, b) => a.label?.localeCompare(b.label)) ?? []
        } as FilterCategory;
    }

    private getRiskTypesFilterGroup(riskCatalogs: RiskCatalog[]): FilterCategory {
        return {
            id: RiskRegistryFilteringGroupEnum.RiskType,
            label: this.contentPipe.transform('risks.riskRegistry.riskTypeFilterGroup'),
            options: riskCatalogs?.map(riskCatalog => ({
                id: riskCatalog.type,
                label: this.sentenceCasePipe.transform(riskCatalog.name),
                selected: false
            })).sort((a, b) => a.label?.localeCompare(b.label)) ?? []
        } as FilterCategory;
    }

    private getQueryParams(filterCategories: FilterCategory[]): RiskRegistryFilters {
        if (!filterCategories) return null;

        let filters: RiskRegistryFilters;
        
        for (let filter of filterCategories) {
            if(filter.id === RiskRegistryFilteringGroupEnum.InherentRisk) {
                filters = { ...filters, inherentRisk: filter.options.map(o => o.id) };
            }

            if(filter.id === RiskRegistryFilteringGroupEnum.ResidualRisk) {
                filters = { ...filters, residualRisk: filter.options.map(o => o.id) };
            }

            if(filter.id === RiskRegistryFilteringGroupEnum.Mitigation) {
                filters = { ...filters, mitigations: filter.options.map(o => o.label)?.join(',') };
            }

            if(filter.id === RiskRegistryFilteringGroupEnum.DataSource) {
                filters = { ...filters, dataSources: filter.options.map(o => o.id).join(',') };
            }

            if(filter.id === RiskRegistryFilteringGroupEnum.RiskType) {
                filters = { ...filters, riskTypes: filter.options.map(o => o.id) };
            }

            if(filter.id === RiskRegistryFilteringGroupEnum.Labels) {
                filters = { ...filters, labels: filter.options.map(o => o.id) };
            }

            if(filter.id === RiskRegistryFilteringGroupEnum.RiskLabels) {
                filters = { ...filters, riskLabels: filter.options.map(o => o.id) };
            }
        }

        return filters;
    }

    setActiveFilters(updatedFilters: FilterCategory[]): void {        
        this.selectFilters().pipe(
            first(),
            tap(filters => this.set({ filters })),
            finalize(() => {
                const reduceFn = (oldState: RiskRegistryTableState) => ({
                    filters: oldState.filters.map(filter => {
                        const id = filter.id;
                        const updatedFilter = updatedFilters.find(f => f.id === id);
                        for (let o of filter.options) {
                            o.selected = updatedFilter?.options.find(option => option.id === o.id)?.selected ?? false;
                        }
                        return filter;
                    })
                });
                this.set(reduceFn);
            })).subscribe();
    }

    updateRisk(updatedRisk: Risk): void {
        this.applyRiskUpdate(updatedRisk);
    }

    /** Merge over the stored row so fields the response omits (the assessment link) survive, then re-resolve the assessment. */
    private applyRiskUpdate(updated: Risk): void {
        if (!updated?.id) {
            return;
        }
        const existing = this.get('riskRegistry')?.get(Number(updated.id));
        const joined = this.withAssessmentPage({ ...existing, ...updated, assessmentPageId: updated.assessmentPageId ?? existing?.assessmentPageId });
        this.upsertRiskInStore({ ...joined, originalState: joined });
    }
}